import { recordProductGuidanceEventBestEffort } from '../../product-guidance/api/product-guidance-analytics.api';
import type {
  ProductGuidanceAnalyticsEvent,
  ProductTourAnalyticsEntryPoint,
  ProductTourAnalyticsStepId,
} from '../../product-guidance/types/product-guidance';

export const PRODUCT_TOUR_ANALYTICS_CONTRACT_ID = 'route42-core-v1' as const;
export const PRODUCT_TOUR_ANALYTICS_VERSION = 1 as const;

const PRODUCT_TOUR_ENTRY_KEY = 'route42:product-tour:entry-point';
type ProductTourAnalyticsEvent = Extract<ProductGuidanceAnalyticsEvent, { event_type: `TOUR_${string}` }>;
type ProductTourAnalyticsInput = ProductTourAnalyticsEvent extends infer Event
  ? Event extends ProductTourAnalyticsEvent
    ? Omit<Event, 'tour_contract_id' | 'tour_version'>
    : never
  : never;

export function rememberProductTourEntryPoint(entryPoint: ProductTourAnalyticsEntryPoint): void {
  try {
    sessionStorage.setItem(PRODUCT_TOUR_ENTRY_KEY, entryPoint);
  } catch {
    // Analytics context is best-effort and must never block the Product Tour.
  }
}

export function getProductTourEntryPoint(): ProductTourAnalyticsEntryPoint {
  try {
    const value = sessionStorage.getItem(PRODUCT_TOUR_ENTRY_KEY);
    if (value === 'ONBOARDING' || value === 'ACE' || value === 'HELP_CENTER' || value === 'TOUR_HOME' || value === 'DIRECT') {
      return value;
    }
  } catch {
    // Fall through to the deterministic direct-entry default.
  }
  return 'DIRECT';
}

export function clearProductTourEntryPoint(): void {
  try {
    sessionStorage.removeItem(PRODUCT_TOUR_ENTRY_KEY);
  } catch {
    // Best-effort analytics context only.
  }
}

export function recordProductTourEventBestEffort(
  event: ProductTourAnalyticsInput,
): void {
  recordProductGuidanceEventBestEffort({
    ...event,
    tour_contract_id: PRODUCT_TOUR_ANALYTICS_CONTRACT_ID,
    tour_version: PRODUCT_TOUR_ANALYTICS_VERSION,
  } as ProductGuidanceAnalyticsEvent);
}

export function analyticsStep(stepId: string | null, stepOrder?: number | null): {
  step_id?: ProductTourAnalyticsStepId;
  step_order?: number;
} {
  if (!stepId || !/^tour-step-\d{2}$/.test(stepId)) return {};
  const parsedOrder = stepOrder ?? Number(stepId.slice(-2));
  if (!Number.isInteger(parsedOrder) || parsedOrder < 1 || parsedOrder > 10) return {};
  return { step_id: stepId as ProductTourAnalyticsStepId, step_order: parsedOrder };
}
